import React from 'react';
import { useState } from 'react';
import {
  Alert,
  AlertColor,
  Backdrop,
  Box,
  Grid,
  Modal,
  Snackbar,
  TextareaAutosize,
  Typography,
} from '@mui/material';
import { CustomButton } from '../../elements/button';
import { CustomInput } from '../../elements/input';
import { RefundStatusForm, TerminatingRefundStatus } from '../../types';
import { CustomSelect } from '../../elements/select';
import { Text } from '../../elements/text';
import { schema } from './validations/form';
import { useMutationHook } from '../../hooks/use-mutation.hook';
import { UPDATE_REFUND } from '../../graphql/mutation';

interface Props {
  // eslint-disable-next-line no-unused-vars
  data: RefundStatusForm;
  openModal: boolean;
  onClose: () => void;
}

export const ManualRefundModal = (props: Props) => {
  const [refundStatus, setRefundStatus] = useState<string>(
    props.data.refundStatus,
  );
  const [amountRefunded, setAmountRefunded] = useState<number | undefined>(
    props.data.amountRefunded,
  );
  const [transactionId, setTransactionId] = useState<string | undefined>(
    props.data.transactionId || '',
  );
  const [closeDate, setCloseDate] = useState<Date | undefined>(
    props.data.closeDate,
  );
  const [reviewerComments, setReviewerComments] = useState<string[]>(
    props.data.issueDescription?.reviewerComments || [],
  );

  const [error, setError] = useState<string | undefined>();

  const defaultOptions = {
    message: '',
    visible: false,
    variant: 'success',
  };
  const [showSnackbar, setShowSnackbar] = useState(defaultOptions);

  const refundSelectData = Object.values(TerminatingRefundStatus).map(
    (refundStatus: TerminatingRefundStatus) => {
      return {
        value: refundStatus,
        label: refundStatus,
      };
    },
  );

  const { mutate: updateRefundStatusRequest } = useMutationHook({
    mutation: UPDATE_REFUND,
    onCompleted: () => {
      setShowSnackbar({
        message: 'Successfully updated',
        visible: true,
        variant: 'success',
      });
      window.location.reload();
    },
    onError: (e) => {
      setShowSnackbar({
        message: 'Faild to updated',
        visible: true,
        variant: 'error',
      });
      console.log(e);
    },
  });

  const submit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (validateDetails()) {
      updateRefundStatusRequest({
        variables: {
          id: props.data.id,
          refundInput: {
            refundStatus: refundStatus,
            amountRefunded: amountRefunded,
            closeDate: closeDate,
            transactionId: transactionId,
            issueDescription: {
              refundReason: props.data.issueDescription.refundReason,
              reportorComments: props.data.issueDescription.reportorComments,
              reviewerComments: reviewerComments,
            },
          },
        },
      });
    }
  };

  const validateDetails = () => {
    const { error } = schema.validate({
      refundStatus: refundStatus,
      amountRefunded: amountRefunded,
      closeDate: closeDate,
      transactionId: transactionId,
      reviewerComments: reviewerComments,
    });

    if (!error) {
      setError('');
      return true;
    }
    setError(error.message);
    return false;
  };

  return (
    <>
      <Snackbar
        open={showSnackbar.visible}
        autoHideDuration={3000}
        onClose={() => {
          setShowSnackbar(defaultOptions);
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
      >
        <Box minWidth={300}>
          <Alert variant="filled" severity={showSnackbar.variant as AlertColor}>
            {showSnackbar.message}
          </Alert>
        </Box>
      </Snackbar>
      <Modal
        open={props.openModal}
        onClose={props.onClose}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            bgcolor: 'background.paper',
            border: '2px solid #000',
            boxShadow: 24,
            overflow: 'auto',
            p: 4,
          }}
          width={'60%'}
        >
          <Typography variant="h4" textAlign={'center'}>
            Record Offline Refund Details
          </Typography>
          {error && <Text text={error} style={{ color: 'red' }} />}
          <form onSubmit={submit}>
            <Grid item xs={12} sm={6} m={'auto'}>
              <CustomSelect
                required
                data={refundSelectData}
                onChange={setRefundStatus}
                title="Refund Status"
                value={refundStatus}
              />
              <Grid mt={2}>
                <CustomInput
                  type="number"
                  min={0}
                  disabled={refundStatus === TerminatingRefundStatus.DENIED}
                  value={amountRefunded}
                  required={
                    refundStatus ===
                      TerminatingRefundStatus.PARTIALLY_REFUNDED ||
                    refundStatus === TerminatingRefundStatus.REFUNDED
                  }
                  onChange={(val) => {
                    setAmountRefunded(val);
                  }}
                  title="Amount Refunded"
                />
              </Grid>
              <Grid mt={2}>
                <CustomInput
                  type="Date"
                  value={
                    closeDate
                      ? new Date(closeDate).toISOString().substring(0, 10)
                      : new Date().toISOString().substring(0, 10)
                  }
                  required
                  onChange={(val) => setCloseDate(val)}
                  title="Close Date"
                />
              </Grid>
              <Grid mt={2}>
                <CustomInput
                  type="text"
                  min={3}
                  value={transactionId}
                  onChange={(val) => setTransactionId(val)}
                  disabled={refundStatus === TerminatingRefundStatus.DENIED}
                  required={
                    refundStatus ===
                      TerminatingRefundStatus.PARTIALLY_REFUNDED ||
                    refundStatus === TerminatingRefundStatus.REFUNDED
                  }
                  title="TransactionId"
                />
              </Grid>
              <Grid mt={2}>
                <TextareaAutosize
                  style={{
                    width: '100%',
                    borderColor: '#c4c4c4',
                    borderRadius: '4px',
                  }}
                  aria-label="reviewer comments"
                  minRows={4}
                  value={reviewerComments}
                  placeholder="Please add your comments"
                  onChange={(event) => {
                    setReviewerComments([event.target.value]);
                  }}
                />
              </Grid>
              <Grid mt={2}>
                <CustomButton title="Submit" />
              </Grid>
            </Grid>
          </form>
        </Box>
      </Modal>
    </>
  );
};
